import { ROUTE_PATHS, getPageTitle } from '@/constants';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Map route segments to display names
const routeNameMap: Record<string, string> = {
  [ROUTE_PATHS.DASHBOARD]: 'Dashboard',
  'daily-roster': 'Daily Roster',
  'add-lesson': 'Add Lesson',
  'transport-roster': 'Transport Roster',
  'lesson-entry': 'Lesson Entry',
  'report-manager': 'Report Manager',
  'instructor-report': 'Instructor Report',
  'financial-report': 'Financial Report',
  'hotel-agents-report': 'Hotel/Agents Report',
  'group-lesson-report': 'Group Lesson Report',
  'mountain-financial-report': 'Mountain Financial Report',
  'mountain-participant-party': 'Mountain Participant Report',
  'staff-report': 'Staff Report',
  'hotel-agent-manager': 'Hotel/Agent Manager',
  all: 'All',
  add: 'Add',
  'commission-rate': 'Manage Commission Rate',
  'booking-manager': 'Booking Manager',
  'all-bookings': 'All Bookings',
  'booking-users': 'Booking Users',
  'broadcast-manager': 'Broadcast Manager',
  'instructor-manager': 'Instructor Manager',
  list: 'List',
  'pay-rate': 'Pay Rate',
  'service-manager': 'Service Manager',
  'mountain-manager': 'Mountain Manager',
  'transport-manager': 'Transport Manager',
  [ROUTE_PATHS.ADMIN_USERS_MANAGER_LIST]: 'Admin Users Manager',
  'cheat-sheet-list': 'Cheat Sheet List',
  'diet-manager': 'Diet Manager',
  contacts: 'Contacts',
  new: 'New Contact',
  edit: 'Edit Contact',
  analytics: 'Analytics',
  settings: 'Settings',
  [ROUTE_PATHS.CHANGE_PASSWORD]: 'Change Password',
  [ROUTE_PATHS.EDIT_PROFILE]: 'Edit Profile',
  'booking-users-list': 'Booking Users List'
};

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);

  // Routes that should not display breadcrumbs
  const noBreadcrumbSegments = new Set([
    'instructor-report',
    'financial-report',
    'hotel-agents-report',
    'group-lesson-report',
    'mountain-financial-report',
    'transport-pax-report',
    'staff-report',
    'mountain-participant-party'
  ]);

  // Check if this is an edit page (has an ID in the path)
  const hasIdInPath = pathnames.some(segment => {
    const isId =
      /^\d+$/.test(segment) || // numeric IDs
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment
      ) || // UUIDs
      /^[0-9a-f]{24}$/i.test(segment); // MongoDB ObjectIds
    return isId;
  });

  // Filter out ID-like segments (numeric or UUID-like strings)
  let filteredPathnames = pathnames.filter(segment => {
    // Keep segments that are in our routeNameMap or don't look like IDs
    if (routeNameMap[segment]) return true;

    // Filter out numeric IDs, UUIDs, or other ID-like patterns
    const isId =
      /^\d+$/.test(segment) || // numeric IDs
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        segment
      ) || // UUIDs
      /^[0-9a-f]{24}$/i.test(segment); // MongoDB ObjectIds

    return !isId;
  });

  // Hide breadcrumbs entirely for specified routes
  const shouldHideBreadcrumbs = pathnames.some(segment =>
    noBreadcrumbSegments.has(segment)
  );
  if (shouldHideBreadcrumbs) {
    filteredPathnames = [];
  }

  // Special case: for instructor roster list, only show Dashboard / Daily Roster
  if (location.pathname.startsWith(ROUTE_PATHS.INSTRUCTOR_ROSTER_LIST)) {
    filteredPathnames = ['daily-roster'];
  }

  // Build each breadcrumb link
  const crumbs = filteredPathnames.map((value, idx) => {
    const to = '/' + filteredPathnames.slice(0, idx + 1).join('/');
    const isLast = idx === filteredPathnames.length - 1;

    // Determine the display name
    let name =
      routeNameMap[value] || value.charAt(0).toUpperCase() + value.slice(1);

    // If this is an edit page and we're showing "add", change it to "edit"
    if (hasIdInPath && value === 'add' && isLast) {
      name = 'Edit';
    }

    return isLast ? (
      <span key={to} className='text-slate-500 font-medium'>
        {name}
      </span>
    ) : (
      <Link
        key={to}
        to={to}
        className='text-blue-700 hover:underline font-medium'
      >
        {name}
      </Link>
    );
  });

  // Always show Dashboard as the root
  return (
    <nav
      className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm py-3 px-6 bg-white/80 border-b border-slate-100'
      aria-label='Breadcrumb'
    >
      {/* Title on the left */}
      <div className='flex items-center'>
        <h1 className='text-lg md:text-xl font-semibold text-gray-900'>
          {getPageTitle(location.pathname)}
        </h1>
      </div>

      {/* Breadcrumb on the right */}
      <div className='flex items-center gap-2 text-xs md:text-sm overflow-x-auto whitespace-nowrap scrollbar-hide'>
        <Link to='/' className='text-blue-700 hover:underline font-medium'>
          Dashboard
        </Link>
        {crumbs.length > 0 && <span className='text-slate-400'>/</span>}
        {crumbs.map((crumb, idx) => (
          <React.Fragment key={idx}>
            {crumb}
            {idx < crumbs.length - 1 && (
              <span className='text-slate-400'>/</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </nav>
  );
};

export default Breadcrumbs;
